import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { ProductCard } from "@/components/ProductCard";
import { SearchBar } from "@/components/SearchBar";
import { ChevronRight, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ShopPage = () => {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get("search") || "";
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        let url = `${API}/products`;
        const params = [];
        if (category) params.push(`category=${category}`);
        if (searchQuery) params.push(`search=${encodeURIComponent(searchQuery)}`);
        if (params.length > 0) url += `?${params.join("&")}`;

        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(url),
          axios.get(`${API}/categories`),
        ]);
        setProducts(productsRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [category, searchQuery]);

  const currentCategory = categories.find((c) => c.slug === category);

  const clearSearch = () => {
    setSearchParams({});
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-deep-moss font-serif text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-stone-200">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl py-4">
          <nav className="flex items-center gap-2 text-sm font-sans" data-testid="breadcrumb">
            <Link to="/" className="text-stone-500 hover:text-deep-moss">
              Home
            </Link>
            <ChevronRight className="w-4 h-4 text-stone-400" />
            <Link to="/shop" className={`${category || searchQuery ? "text-stone-500 hover:text-deep-moss" : "text-stone-900"}`}>
              Shop
            </Link>
            {currentCategory && (
              <>
                <ChevronRight className="w-4 h-4 text-stone-400" />
                <span className="text-stone-900">{currentCategory.name}</span>
              </>
            )}
            {searchQuery && (
              <>
                <ChevronRight className="w-4 h-4 text-stone-400" />
                <span className="text-stone-900">Search: &ldquo;{searchQuery}&rdquo;</span>
              </>
            )}
          </nav>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-12">
        <div className="flex flex-col md:flex-row gap-12">
          {/* Sidebar */}
          <aside className="md:w-64 flex-shrink-0">
            {/* Search on Shop Page */}
            <div className="mb-8">
              <SearchBar />
            </div>

            <h2 className="font-serif text-xl font-semibold text-stone-900 mb-6">Categories</h2>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/shop"
                  data-testid="filter-all"
                  className={`block font-sans text-sm py-2 border-b border-transparent transition-colors ${
                    !category && !searchQuery ? "text-deep-moss font-medium border-deep-moss" : "text-stone-600 hover:text-deep-moss"
                  }`}
                >
                  All Products
                </Link>
              </li>
              {categories.map((cat) => (
                <li key={cat.id}>
                  <Link
                    to={`/shop/${cat.slug}`}
                    data-testid={`filter-${cat.slug}`}
                    className={`block font-sans text-sm py-2 border-b border-transparent transition-colors ${
                      category === cat.slug
                        ? "text-deep-moss font-medium border-deep-moss"
                        : "text-stone-600 hover:text-deep-moss"
                    }`}
                  >
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </aside>

          {/* Products Grid */}
          <main className="flex-1">
            <div className="flex items-end justify-between mb-8">
              <div>
                <h1 className="font-serif text-3xl md:text-4xl font-semibold text-stone-900">
                  {searchQuery ? `Results for "${searchQuery}"` : currentCategory ? currentCategory.name : "All Products"}
                </h1>
                <p className="font-sans text-stone-600 mt-2">
                  {products.length} product{products.length !== 1 ? "s" : ""}
                </p>
              </div>
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  data-testid="clear-search"
                  className="flex items-center gap-1 text-sm text-stone-500 hover:text-deep-moss"
                >
                  <X className="w-4 h-4" />
                  Clear search
                </button>
              )}
            </div>

            {products.length === 0 ? (
              <div className="text-center py-16">
                <p className="font-sans text-stone-600">
                  {searchQuery 
                    ? `No products found for "${searchQuery}". Try a different search term.`
                    : "No products found in this category."
                  }
                </p>
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="mt-4 text-deep-moss font-sans text-sm hover:underline"
                  >
                    View all products
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};
